import React from "react";
import { Box, Typography, Stack, LinearProgress } from "@mui/material";
import { LeadThread, AI_SCORE_CONFIG } from "./leadApi";
import { getSource } from "./leadCellUtils";

const CREATOR_SOURCES = new Set(["phone", "walk_in", "manual", "referral"]);

function resolveCreatorName(lead: LeadThread): string | null {
  const c = lead.creator;
  if (!c) return null;
  const full = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
  return full || c.username || null;
}

interface Props {
  lead: LeadThread;
}

/**
 * Score column: AI score as a small dot + label, source as a muted
 * neutral pill. The earlier linear-progress bar was removed — it
 * felt like a fitness app readout, not an enterprise CRM signal.
 */
export const ScoreSourceCell = React.memo(function ScoreSourceCell({ lead }: Props) {
  const src = getSource(lead.source);

  return (
    <Stack spacing={0.7} alignItems="flex-start" sx={{ width: "100%" }}>
      {/* AI score: ● Label + thin progress meter.
          The meter is intentionally muted — a hairline-thin 2px track
          with subtle tinted fill — so it reads as a confidence
          indicator, not a fitness-tracker bar. */}
      {lead.ai_score ? (() => {
        const cfg = AI_SCORE_CONFIG[lead.ai_score];
        const pct = lead.ai_score === "hot" ? 90 : lead.ai_score === "warm" ? 55 : 20;
        return (
          <Box sx={{ width: "100%", maxWidth: 90 }}>
            <Stack direction="row" alignItems="center" gap={0.6} mb={0.35}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: cfg.color, flexShrink: 0 }} />
              <Typography sx={{ fontSize: "0.74rem", fontWeight: 700, color: cfg.color }}>
                {cfg.label}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate" value={pct}
              sx={{
                height: 2, borderRadius: 2,
                bgcolor: "#f1f5f9",
                "& .MuiLinearProgress-bar": { bgcolor: cfg.color, borderRadius: 2 },
              }}
            />
          </Box>
        );
      })() : (
        <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8" }}>—</Typography>
      )}

      {/* Source pill — muted, no colored icon background */}
      <Box sx={{
        display: "inline-flex", alignItems: "center", gap: 0.4,
        bgcolor: "#fcfcfd",
        border: "1px solid #e5e7eb",
        borderRadius: "6px",
        px: 0.7, py: "1px",
      }}>
        <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: "#475569", letterSpacing: 0.2 }}>
          {src.label}
        </Typography>
      </Box>

      {CREATOR_SOURCES.has(lead.source) && (() => {
        const name = resolveCreatorName(lead);
        return name ? (
          <Typography sx={{ fontSize: "0.62rem", color: "#94a3b8", lineHeight: 1.3 }}>
            by {name}
          </Typography>
        ) : null;
      })()}
    </Stack>
  );
});
